import { api } from '@/Data/api';
import ss from '../Session';
import { BotType, BotResponse, MatchHistoryResponse, MatchHistory } from './types';

export * from './types';

/**
 * Get a single bot profile by ID
 * Uses GET /bot/profile?botid=xxx
 */
export const getBot = async (botid: string): Promise<BotType | null> => {
    try {
        const res = await api.get<BotResponse<BotType>>('/bot/profile', {
            params: { botid },
            headers: { Authorization: `Bearer ${ss.getCurrentSession().jwt}` }
        });
        return res.data?.code === 200 ? (res.data.data ?? null) : null;
    } catch (e) {
        console.error('Failed to get bot:', e);
        return null;
    }
};

/**
 * Get multiple bot profiles by IDs
 */
export const getBots = async (botids: string[]): Promise<BotType[]> => {
    try {
        const results = await Promise.all(botids.map(botid => getBot(botid)));
        return results.filter((bot): bot is BotType => bot !== null);
    } catch (e) {
        console.error('Failed to get bots:', e);
        return [];
    }
};

/**
 * List all bot profiles (requires bulk=true)
 * Uses GET /bot/profile?bulk=true
 */
export const listBots = async (): Promise<BotType[]> => {
    try {
        const res = await api.get<BotResponse<BotType[]>>('/bot/profile', {
            params: { bulk: 'true' },
            headers: { Authorization: `Bearer ${ss.getCurrentSession().jwt}` }
        });
        return res.data?.code === 200 ? (res.data.data ?? []) : [];
    } catch (e) {
        console.error('Failed to list bots:', e);
        return [];
    }
};

/**
 * Get match history for current user
 * Uses GET /match/history
 */
export const getMatchHistory = async (): Promise<MatchHistory[]> => {
    try {
        const res = await api.get<MatchHistoryResponse>('/match/history', {
            headers: { Authorization: `Bearer ${ss.getCurrentSession().jwt}` }
        });
        return res.data?.code === 200 ? (res.data.data ?? []) : [];
    } catch (e) {
        console.error('Failed to get match history:', e);
        return [];
    }
};

/**
 * Get recommended bots - checks history first, then gets new recommendations if needed
 * Uses GET /match/history first, then POST /match/recommend if no history
 * @param limit Maximum number of bots to return (default 3)
 */
export const getRecommendedBots = async (limit: number = 3): Promise<BotType[]> => {
    try {
        // First check match history
        const history = await getMatchHistory();

        if (history.length > 0) {
            // Have history - fetch bot profiles for those botids
            const botids = history.slice(0, limit).map(h => h.botid);
            return await getBots(botids);
        }

        // No history - get new recommendations
        const res = await api.post<BotResponse<BotType[]>>('/match/recommend', {
            status: 'active',
            limit
        }, {
            headers: { Authorization: `Bearer ${ss.getCurrentSession().jwt}` }
        });
        return res.data?.code === 200 ? (res.data.data ?? []) : [];
    } catch (e) {
        console.error('Failed to get recommended bots:', e);
        return [];
    }
};
